import Vue from "vue";
import Vuex from "vuex";
import orderiomApiPackage from "orderiom-api-package";

Vue.use(Vuex);
const previousQRCodes = JSON.parse(localStorage.getItem('previous-qr-codes') || '[]');

const store = new Vuex.Store({
  state: {
    isAuthorized: true,
    table: null,
    hideBasketFromHeader: true,
    saveQrcodeList: previousQRCodes,
    announcementId: null,
		isCategoryCarouselShown: false,
		isDarkMode: false,
		legalDialogToShow: '',
		isFetchingLegalInfo: false,
    deliveryCloseMessage: null,
    predefinedComments: [],
    predefinedCommentsRestaurantId: null,
    fetchingPredefinedComments: false,
  },
  getters: {
		restaurantSlug(state, getters){
			return (
				getters.restaurantInfo.slug ||
				state.orderiomApiPackage.order.order?.restaurant_slug
			);
		},
		informationFields(state, getters) {
			return getters.restaurantInfo.personal_information_fields;
		},
		imprint(state){
			return state.orderiomApiPackage.restaurant.LegalContent?.imprint || ''
		},
		privacy(state){
			return state.orderiomApiPackage.restaurant.LegalContent?.privacy_policy || ''
		},
		terms(state){
			return state.orderiomApiPackage.restaurant.LegalContent?.terms_and_conditions || ''
		},
		about(state){
			return state.orderiomApiPackage.restaurant.LegalContent?.about_us || ''
		},
		logoLink(state, getters) {
			let route = `/restaurant/${getters.restaurantInfo.slug}`;

			const tableName = state.orderiomApiPackage.product.basketInfo?.table?.name;
			if (tableName) route += `?tableName=${tableName}`;

			return route;
		},
    deliveryTypes(_, getters){
      // deliveryTypes here must be dependant to other restaurant configs such as 'closed'
      const result = getters.restaurantInfo.deliveryTypes || [];
      if(getters.restaurantInfo.closed){
        return result.filter(item => item !== 'here');
      }
      return result;
    },
    supportsTip(state, getters){
      return !!getters.restaurantInfo.tip_request;
    },
		supportsVoucher(state, getters) {
			return !!getters.restaurantInfo.available_voucher;
		},
		categories(state){
			return state.orderiomApiPackage.product.categories || [];
		},
    shoppingCart(state){
      return state.orderiomApiPackage.product.ShoppingCart;
    },
		cartItems(state, getters){
			return getters.basketInfo.products || [];
		},
		cartItemsCount(state, getters) {
			return getters.cartItems
				.map(p => p.quantity)
				.reduce((a, b) => a + b, 0);
		},
    closed(state){
      return state.orderiomApiPackage.restaurant.restaurantInfo.closed
    },
    currency(state){
      return state.orderiomApiPackage.restaurant.restaurantInfo.currency;
    },
    restaurantInfo(state){
      return state.orderiomApiPackage.restaurant.restaurantInfo;
    },
    supportsItemComment(state, getters){
      return !!getters.restaurantInfo.personal_information_fields?.product_comment;
    },
    predefinedComments(state){
      return state.predefinedComments;
    },
    fetchingPredefinedComments(state){
      return state.fetchingPredefinedComments;
    },
    basketInfo(state){
      return state.orderiomApiPackage.product.basketInfo || {};
    },
    deliveryTime(_, getters){
      return getters.basketInfo.deliveryTime || null;
    },
    loadingBasket(state){
      return !state.orderiomApiPackage.product.basketLoadedOnce || state.orderiomApiPackage.product.fetchingBasket;
    },
    deliveryType(_, getters){
      const id = getters.basketInfo.deliveryType || null;
      switch (id){
        case 1:
          return 'delivery';
        case 2:
          return 'pickup';
        case 3:
          return 'here';
        default:
          return null;
      }
    },
    paymentMethodNames(_, getters) {
      return getters.restaurantInfo.paymentMethodNames || {};
    },
    paymentMethodImages(_, getters) {
      return getters.restaurantInfo.paymentMethodImages || {};
    },
    hasAnnouncement(state, getters){
      return Boolean(getters.restaurantInfo?.has_announcement);
    },
    announcementText(state, getters) {
      return getters.restaurantInfo.announcement || '';
    },
    checkoutAnnouncement(state, getters){
      return getters.restaurantInfo.announcement_before_checkout || '';
    },
    closedText(state, getters){
      return getters.restaurantInfo?.closeMessage;
    },
    deliveryCloseMessage(state, getters) {
      if (getters.deliveryType === 'delivery' && getters.restaurantInfo.delivery_close_message) {
       return getters.restaurantInfo.delivery_close_message;
      }
      if (getters.deliveryType === 'pickup' && getters.restaurantInfo.pickup_close_message) {
        return getters.restaurantInfo.pickup_close_message;
      }
      if (getters.deliveryType === 'here' && getters.restaurantInfo.here_close_message) {
        return getters.restaurantInfo.here_close_message;
      }
      return null;
    }
  },
  mutations: {
		setDarkMode(state, value){
			state.isDarkMode = !!value;
		},
		updateCategoryCarouselState(state, isShown){
			state.isCategoryCarouselShown = isShown;
		},
    removeExternalOrderTable(state, _) {
      const externalOrderTable = localStorage.getItem("externalOrderTable");
      if (externalOrderTable) {
        const json = JSON.parse(externalOrderTable);
        delete json[state.orderiomApiPackage.restaurant.restaurantInfo.slug];
        localStorage.setItem("externalOrderTable", JSON.stringify(json));
      }
    },
    setTable(state, table) {
      state.table = table;
    },
    setHideBasketFromHeader(state, status) {
      state.hideBasketFromHeader = status;
    },
    setSaveQrcodeList(state, { qrcode, pin }) {
      state.saveQrcodeList.push({ qrcode, pin });
      localStorage.setItem('previous-qr-codes', JSON.stringify(state.saveQrcodeList))
    },
    deleteItem(state, index) {
      state.saveQrcodeList.splice(index, 1);
      localStorage.setItem('previous-qr-codes', JSON.stringify(state.saveQrcodeList))
    },
    updateAnnouncementId(state, id){
      state.announcementId = id;
    },
		setLegalDialog(state, value){
			state.legalDialogToShow = value;
		},
		setFetchingLegalInfo(state, value){
			state.isFetchingLegalInfo = value;
		},
		setPredefinedComments(state, {comments, restaurantId}){
			state.predefinedComments = comments;
			state.predefinedCommentsRestaurantId = restaurantId;
		},
		setFetchingPredefinedComments(state, value){
			state.fetchingPredefinedComments = value;
		},
  },
  actions: {
		async fetchPredefinedComments({state, commit}, restaurantId){
			const normalizedRestaurantId = Number(restaurantId);
			if(!normalizedRestaurantId) return [];

			if(
				state.predefinedCommentsRestaurantId === normalizedRestaurantId
			) {
				return state.predefinedComments;
			}

			commit('setFetchingPredefinedComments', true);
			try {
				const response = await orderiomApiPackage.$http.get(
					'api/restaurant/predefined-comments',
					{params: {restaurantId: normalizedRestaurantId}}
				);
				const comments = Array.isArray(response.data?.data) ? response.data.data : [];

				commit('setPredefinedComments', {
					comments,
					restaurantId: normalizedRestaurantId,
				});
				return comments;
			} catch(error) {
				commit('setPredefinedComments', {comments: [], restaurantId: null});
				console.error('Unable to fetch predefined comments.', error);
				return [];
			} finally {
				commit('setFetchingPredefinedComments', false);
			}
		},
		async fetchLegalInfo({dispatch, commit}){
			commit('setFetchingLegalInfo', true);
			await dispatch("restaurant/getLegalContent")
			commit('setFetchingLegalInfo', false);
		},
		openLegalDialog({commit}, value){
			switch (value){
				case 'imprint':
				case 'privacy_policy':
				case 'about_us':
				case 'terms_and_conditions':
					commit('setLegalDialog', value);
					break;
				default:
					commit('setLegalDialog', '');
			}
		},
		closeLegalDialog({commit}){
			commit('setLegalDialog', '');
		},
		setCategoryCarousel({ commit }, isShown){
			commit('updateCategoryCarouselState', isShown);
		},
    hideBasketFromHeader({ commit }, status) {
      commit("setHideBasketFromHeader", status);
    },
    updateSaveQrcodeList({ commit }, qrcode) {
      commit("setSaveQrcodeList", qrcode);
    },
    deleteSaveQrcodeList({ commit }, itemIndex) {
      commit("deleteItem", itemIndex);
    },
    setAnnouncementId({commit}, id){
      commit('updateAnnouncementId', id);
    },
		setIsDark({commit}, value){
			commit('setDarkMode', value);
		}
  },
  modules: {},
});

Vue.use(orderiomApiPackage, { store })

export default store;
